/**
 * FlowOverlaySortControls — flow order vs rank sort for overlay rows.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';

import { cn } from '@/lib/utils';
import type { PreviewRankFilter } from '@/features/flow-optimizer/components/preview/preview-sort';

import { useFlowOverlaySort } from './FlowOverlaySortContext';
import type { FlowOverlayListSortMode } from './flow-overlay-sort.util';

const SORT_OPTIONS: ReadonlyArray<{ id: FlowOverlayListSortMode; label: string }> = [
  { id: 'flow', label: 'Flow order' },
  { id: 'rank', label: 'By rank' },
];

const FILTER_OPTIONS: ReadonlyArray<{ id: PreviewRankFilter; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'top5', label: 'Top 5' },
  { id: 'top10', label: 'Top 10' },
];

const FlowOverlaySortControls: FC = () => {
  const sort = useFlowOverlaySort();
  if (!sort) return null;

  return (
    <div className="flex flex-wrap items-center gap-2 px-3 sm:px-4">
      <ControlGroup
        label="List order"
        options={SORT_OPTIONS}
        value={sort.sortMode}
        onChange={sort.setSortMode}
      />
      {sort.sortMode === 'rank' ? (
        <ControlGroup
          label="Show"
          options={FILTER_OPTIONS}
          value={sort.rankFilter}
          onChange={sort.setRankFilter}
        />
      ) : null}
    </div>
  );
};

interface ControlGroupProps<T extends string> {
  label: string;
  options: ReadonlyArray<{ id: T; label: string }>;
  value: T;
  onChange: (next: T) => void;
}

function ControlGroup<T extends string>({
  label,
  options,
  value,
  onChange,
}: ControlGroupProps<T>) {
  return (
    <div className="flex flex-wrap items-center gap-1">
      <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      {options.map((option) => (
        <button
          key={option.id}
          type="button"
          onClick={() => onChange(option.id)}
          className={cn(
            'rounded border px-2 py-0.5 text-[11px] tabular-nums transition-colors',
            value === option.id
              ? 'border-primary/40 bg-primary/10 text-foreground'
              : 'border-border/60 bg-background text-muted-foreground hover:bg-muted/40',
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

export default FlowOverlaySortControls;
